import React from 'react';
import { useState, useRef, useEffect } from 'react';
import { Dropdown, Navbar, Container, Nav, NavDropdown } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthState } from '../../services/auth';
import './navbar.scss';
import { isMobile } from 'react-device-detect';
import { useOperatorsState } from '../../services/OperatorsList';
import axios from 'axios';
import { useEnterpriseStore } from '../../stores/enterprise.store';
import {
  Activity,
  Check,
  Crown,
  Home,
  LogOut,
  LucideIcon,
  MonitorSmartphone,
  Moon,
  Settings,
  ShieldAlert,
  Sun,
  Users,
  Wrench,
} from 'lucide-react';
import '../../design-system/index.css';
import { ACCENT_OPTIONS, useAccent } from '../../design-system/accent';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, getLanguage } from '../../util/languages';

const routeIcons: Record<string, LucideIcon> = {
  Master: Crown,
  Home: Home,
  Accounts: Users,
  Devices: MonitorSmartphone,
  Threats: ShieldAlert,
  Activity: Activity,
  Tools: Wrench,
  Settings: Settings,
};

// Language name in the UI's language ("Swedish" / "Sueco"), from the browser's Intl data
const languageDisplayName = (locale: string, uiLocale: string) => {
  try {
    const name = new Intl.DisplayNames([uiLocale], { type: 'language' }).of(locale.split('-')[0]);
    return name ? name.charAt(0).toLocaleUpperCase(uiLocale) + name.slice(1) : '';
  } catch {
    return '';
  }
};

const CustomNavBar = (props: any) => {
  const [accent, setAccent] = useAccent();
  const { t, i18n } = useTranslation();
  const currentLanguage = getLanguage(i18n.language);
  const { operatorId } = useParams();
  const { isAuthenticated } = useAuthState();
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const [isActivity, setIsActivity] = useState(false); // DNS Activity visibility
  const location = useLocation();
  const { operatorList, loggedUserData, setOperatorList, setLoggedUserData } = useOperatorsState();
  const navigate = useNavigate();
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const enterprise = useEnterpriseStore((state) => state.enterprise);
  const loaded = useEnterpriseStore((state) => state.loaded);
  const reset = useEnterpriseStore((state) => state.reset);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isMaster, setIsMaster] = useState<boolean>(false);
  const [block, setIsBlocked] = useState<boolean>(false);

  /** =========================
   *  FETCH DNS ACTIVITY TAB STATUS
   *  ========================= */
  async function fetchDnsActivityStatus() {
    try {
      const id = loggedUserData?.operatorId || operatorId;
      if (!id) return;

      const response = await axios.get(`/v2/op/master-operator/${id}/fetch-retina-dns-visibility`);
      if (response?.data?.retinaDns !== undefined) {
        // If retinaDns = 1 → show Activity tab
        setIsActivity(response.data.retinaDns === 1);
      }
    } catch (error) {
      console.error('Error fetching DNS Activity visibility:', error);
    }
  }

  useEffect(() => {
    if (loggedUserData?.operatorId) {
      fetchDnsActivityStatus();
    }
  }, [loggedUserData?.operatorId]);

  /** =========================
   *  FETCH MASTER STATUS
   *  ========================= */
  const getEmailFromLocalStorage = (): string | null => {
    const email = localStorage.getItem('email');
    setUserEmail(email);
    return email ? email : null;
  };

  const fetchMasterStatus = async (email: string) => {
    try {
      const response = await axios.get(`/v2/op/master-operator/master?email=${email}`);
      setIsMaster(response.data.masterControl);
    } catch (error) {
      console.error('Error fetching master status:', error);
    }
  };

  useEffect(() => {
    const email = getEmailFromLocalStorage();
    if (email) {
      fetchMasterStatus(email);
    }
  }, []);

  /** =========================
   *  LOGOUT FUNCTION
   *  ========================= */
  function logout() {
    window.localStorage.clear();
    setOperatorList(null);
    reset();
    setLoggedUserData(null);
    navigate('login');
  }

  /** =========================
   *  NAVIGATION ROUTES
   *  ========================= */
  const routeList = [
    ...(isMaster
      ? [
          {
            name: 'Master',
            path: `/operators/`,
          },
        ]
      : []),
    {
      name: 'Home',
      path: `/operators/${loggedUserData?.operatorId}`,
    },
    {
      name: 'Accounts',
      path: `/general-list/${loggedUserData?.operatorId}/operatorcpe/Total Accounts Managed`,
    },
    {
      name: 'Devices',
      path: `/general-list/${loggedUserData?.operatorId}/operatordevices/Total Devices Managed`,
    },
    {
      name: 'Threats',
      path: `/threatsmanaged/${loggedUserData?.operatorId}`,
    },
    // 👇 Show Activity only if retinaDns === 1
    ...(isActivity
      ? [
          {
            name: 'Activity',
            path: `/dnsactivity/${loggedUserData?.operatorId}`,
          },
        ]
      : []),
    {
      name: 'Tools',
      path: `/operator-tools/${loggedUserData?.operatorId}`,
    },
    {
      name: 'Settings',
      path: `/operators/settings`,
    },
  ];

  /** =========================
   *  HANDLE MENU ITEM CLICK
   *  ========================= */
  function onClickMenuItem(item: any) {
    window.localStorage.setItem('currentNav', item.name);
    // Client-side navigation for every item (incl. Master) — a hard
    // `window.location.href` reload re-mounted the whole app and flashed the navbar.
    navigate(item.path);
  }

  /** =========================
   *  RENDER NAVBAR
   *  ========================= */
  const email = window.localStorage.getItem('email') || '';

  // Brand chrome: the bar always uses the design-system dark tokens so the logo stays legible
  return loggedUserData && isAuthenticated && location.pathname !== '/landing' ? (
    <div
      id="CustomNavBar"
      className={'bd' + (isMobile ? ' mobile' : '') + (isNavExpanded ? ' custom-expanded' : '')}
      data-theme="dark"
      data-accent="mint"
    >
      <Navbar expand="xl" className="navbar navbar-dark" onToggle={setIsNavExpanded}>
        <Container fluid className="nav-inner">
          <Navbar.Brand
            href={`/operators/${loggedUserData?.operatorId}`}
            className="nav-brand"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              window.localStorage.setItem('currentNav', 'Home');
              navigate(`/operators/${loggedUserData?.operatorId}`);
            }}
          >
            <img src="/icons/frame.svg" alt="BLACKDICE-LOGO" />
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="navbarScroll" className="nav-toggle" />
          <Navbar.Collapse id="navbarScroll" className="items-center">
            <Nav className="nav-routes mx-auto" navbarScroll>
              {routeList.map((route) => {
                const Icon = routeIcons[route.name];
                const active = props.isActive(route?.name);
                const label = t(`nav.${route.name.toLowerCase()}`, route.name);
                return (
                  <Nav.Link
                    key={route.path}
                    className={`navbar-button ${active ? 'active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => onClickMenuItem(route)}
                  >
                    {Icon && <Icon size={15} strokeWidth={1.9} aria-hidden="true" />}
                    <span className="nav-route-label">{label}</span>
                  </Nav.Link>
                );
              })}
            </Nav>

            <Nav className="nav-actions">
              <NavDropdown
                align="end"
                id="navbarScrollingDropdown"
                className="nav-lang"
                title={
                  <>
                    <img className="nav-lang-flag" src={currentLanguage.flag} alt="" />
                    <span className="visually-hidden">{t('nav.language')}: </span>
                    <span className="bd-mono" aria-hidden="true">
                      {currentLanguage.short}
                    </span>
                    <span className="visually-hidden" lang={currentLanguage.locale}>
                      {currentLanguage.name}
                    </span>
                  </>
                }
              >
                <div className="nav-lang-hd" aria-hidden="true">
                  {t('nav.language')}
                </div>
                {LANGUAGES.map((language) => {
                  const selected = language.code === currentLanguage.code;
                  // e.g. "Swedish" when the UI is English; hidden when it matches the native name
                  const localName = languageDisplayName(language.locale, currentLanguage.locale);
                  return (
                    <NavDropdown.Item
                      key={language.code}
                      as="button"
                      type="button"
                      className="nav-lang-option"
                      active={selected}
                      aria-current={selected ? 'true' : undefined}
                      onClick={() => i18n.changeLanguage(language.code)}
                    >
                      <img className="nav-lang-option-flag" src={language.flag} alt="" />
                      <span className="nav-lang-option-text">
                        <span className="nav-lang-option-name" lang={language.locale}>
                          {language.name}
                        </span>
                        {localName && localName !== language.name && (
                          <span className="nav-lang-option-sub">{localName}</span>
                        )}
                      </span>
                      {selected && <Check className="nav-lang-option-check" size={16} strokeWidth={2.4} aria-hidden="true" />}
                    </NavDropdown.Item>
                  );
                })}
              </NavDropdown>

              <div
                className="nav-accents"
                role="radiogroup"
                aria-label={t('nav.accent')}
                onKeyDown={(e) => {
                  const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
                  if (!step) return;
                  e.preventDefault();
                  const n = ACCENT_OPTIONS.length;
                  const next = ACCENT_OPTIONS[(ACCENT_OPTIONS.indexOf(accent) + step + n) % n];
                  setAccent(next);
                  (e.currentTarget.querySelector(`.nav-accent.${next}`) as HTMLElement | null)?.focus();
                }}
              >
                {ACCENT_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    tabIndex={accent === option ? 0 : -1}
                    aria-checked={accent === option}
                    aria-label={`${option} accent`}
                    title={option.charAt(0).toUpperCase() + option.slice(1)}
                    className={`nav-accent ${option}`}
                    onClick={() => setAccent(option)}
                  />
                ))}
              </div>

              <button
                type="button"
                className="bd-iconbtn nav-iconbtn"
                onClick={props.toggleTheme}
                title={props.darkMode ? t('nav.lightMode') : t('nav.darkMode')}
                aria-label={props.darkMode ? t('nav.lightMode') : t('nav.darkMode')}
              >
                {props.darkMode ? <Sun size={16} /> : <Moon size={16} />}
              </button>

              <div className="nav-user" title={email}>
                <span className="bd-avatar sm">{(email || '?').charAt(0).toUpperCase()}</span>
                <span className="nav-user-email">{email}</span>
              </div>

              <button type="button" className="bd-iconbtn nav-iconbtn nav-logout" onClick={logout} title={t('nav.logout')} aria-label={t('nav.logout')}>
                <LogOut size={16} />
              </button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </div>
  ) : null;
};

export default CustomNavBar;
